import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import ContactForm from '@/components/sections/ContactForm';
import { SITE_CONFIG } from '@/data/siteData';
import { getFrSeoPageBySlug, FR_SEO_SECTION_LINKS } from '@/data/frSeoPages';
import { Phone, MessageCircle, MapPin, CheckCircle, ArrowRight, Home, Mail } from 'lucide-react';

const FrSEOPage = () => {
  const location = useLocation();
  const path = location.pathname.replace(/^\//, '');
  const pageData = getFrSeoPageBySlug(path);

  if (!pageData) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center py-20">
            <h1 className="text-4xl font-bold text-[#1a365d] mb-4">Page non trouvée</h1>
            <p className="text-gray-600 mb-8">La page que vous recherchez n'existe pas.</p>
            <Link to="/" className="text-[#D4AF37] hover:underline">Retour à l'accueil</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const canonicalUrl = `${window.location.origin}/${pageData.slug}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <html lang="fr" />
        <title>{pageData.title}</title>
        <meta name="description" content={pageData.description} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hreflang="fr" href={canonicalUrl} />
        <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={pageData.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": pageData.h1,
            "description": pageData.description,
            "provider": {
              "@type": "LocalBusiness",
              "name": SITE_CONFIG.companyName,
              "telephone": SITE_CONFIG.phone,
              "email": SITE_CONFIG.email,
              "address": {
                "@type": "PostalAddress",
                "addressLocality": pageData.city,
                "addressRegion": "Provence-Alpes-Côte d'Azur",
                "addressCountry": "FR"
              }
            },
            "areaServed": pageData.city
          })}
        </script>
      </Helmet>

      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#1a365d] to-[#0f2444] text-white py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#D4AF37] font-medium">{pageData.city}</span>
                </div>
                <h1 
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  data-testid="fr-seo-page-h1"
                >
                  {pageData.h1}
                </h1>
                <p className="text-lg text-slate-300 mb-8">
                  {pageData.description}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={SITE_CONFIG.phoneLink}
                    className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1a365d] px-6 py-3 rounded-lg font-semibold hover:bg-[#c9a030] transition-colors"
                    data-testid="fr-cta-phone"
                  >
                    <Phone className="w-5 h-5" />
                    Appeler maintenant
                  </a>
                  <a
                    href={SITE_CONFIG.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors"
                    data-testid="fr-cta-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </a>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                  <h3 className="text-xl font-semibold mb-6 text-[#D4AF37]">Pourquoi nous choisir ?</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>Personnel qualifié et de confiance</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>Intervention rapide sous 24-48h</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>Devis gratuit et sans engagement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span>Satisfaction garantie</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <article 
              className="prose prose-lg max-w-none
                prose-headings:text-[#1a365d] prose-headings:font-semibold
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-gray-700 prose-p:leading-relaxed
                prose-li:text-gray-700
                prose-strong:text-[#1a365d]
                prose-ul:my-4 prose-li:my-1"
              dangerouslySetInnerHTML={{ __html: pageData.content }}
              data-testid="fr-seo-page-content"
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#f8f5f0] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 
              className="text-3xl font-bold text-[#1a365d] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Demandez votre devis gratuit
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Contactez-nous dès maintenant pour obtenir un devis personnalisé sous 24h.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href={SITE_CONFIG.phoneLink}
                className="inline-flex items-center justify-center gap-2 bg-[#1a365d] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#0f2444] transition-colors"
              >
                <Phone className="w-5 h-5" />
                {SITE_CONFIG.phone}
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1a365d] px-8 py-4 rounded-lg font-semibold hover:bg-[#c9a030] transition-colors"
              >
                Formulaire de contact
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 
              className="text-2xl font-bold text-[#1a365d] mb-8 text-center"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Nos services de ménage par ville
            </h2>
            
            {/* City sections */}
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              {/* Fréjus */}
              <div className="bg-[#f8f5f0] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Fréjus</h3>
                <ul className="space-y-2">
                  {FR_SEO_SECTION_LINKS.filter(l => l.href.includes('frejus')).map((link, i) => (
                    <li key={i}>
                      <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Saint-Raphaël */}
              <div className="bg-[#f8f5f0] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Saint-Raphaël</h3>
                <ul className="space-y-2">
                  {FR_SEO_SECTION_LINKS.filter(l => l.href.includes('saint-raphael')).map((link, i) => (
                    <li key={i}>
                      <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Cannes */}
              <div className="bg-[#f8f5f0] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#1a365d] mb-4">Cannes</h3>
                <ul className="space-y-2">
                  {FR_SEO_SECTION_LINKS.filter(l => l.href.includes('cannes')).map((link, i) => (
                    <li key={i}>
                      <Link to={link.href} className="text-gray-600 hover:text-[#D4AF37] text-sm transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Main navigation links */}
            <div className="flex flex-wrap justify-center gap-6 pt-6 border-t border-gray-200">
              <Link to="/" className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline">
                <Home className="w-4 h-4" />
                Accueil
              </Link>
              <Link to="/#contact" className="inline-flex items-center gap-2 text-[#D4AF37] hover:underline">
                <Mail className="w-4 h-4" />
                Contact
              </Link>
              <Link to="/blog" className="text-[#D4AF37] hover:underline">Blog</Link>
              <Link to="/nettoyage-frejus" className="text-[#D4AF37] hover:underline">Nettoyage Fréjus</Link>
              <Link to="/nettoyage-saint-raphael" className="text-[#D4AF37] hover:underline">Nettoyage Saint-Raphaël</Link>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <div id="contact">
          <ContactForm />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FrSEOPage;
